import { FC, useState } from 'react'
import { Button, LoadingIndicator, useModal } from 'app/components'
import {
  ButtonType,
  IconPaths,
  KTSVG,
  showAlert,
  showConfirmAlert,
  showError,
} from '_metronic/helpers'
import { EsusConfiguracaoView, EsusPendenciaDto } from '../../core/_esusModels'
import {
  ativarEsusIntegracao,
  desativarEsusIntegracao,
  getEsusChecklist,
} from '../../core/_requests'
import {
  isPermissaoNegadaEsus,
  MSG_SEM_PERMISSAO_ESUS,
  usePermissaoEsusConfig,
} from '../../core/_esusPermissao'
import { EsusChecklistModal } from './EsusChecklistModal'

type Props = {
  empresaId: number
  unidadeId: number
  config: EsusConfiguracaoView | undefined
  onConfigChanged: (updated: EsusConfiguracaoView) => void
}

export const EsusStatusCard: FC<Props> = ({ empresaId, unidadeId, config, onConfigChanged }) => {
  const { showModal } = useModal()
  const [loading, setLoading] = useState(false)
  const podeEditar = usePermissaoEsusConfig()

  const alertarSemPermissao = () =>
    showAlert({ title: 'Sem permissão', text: MSG_SEM_PERMISSAO_ESUS, icon: 'warning' })

  const openChecklistModal = (pendencias: EsusPendenciaDto[]) => {
    showModal({
      title: 'Checklist do Gate de Ativação e-SUS',
      content: <EsusChecklistModal pendencias={pendencias} />,
    })
  }

  const handleVerificarChecklist = async () => {
    setLoading(true)
    try {
      const checklist = await getEsusChecklist(empresaId, unidadeId)
      if (checklist.aptoParaAtivacao) {
        showAlert({
          title: 'Unidade Apta!',
          text: 'Todos os requisitos cadastrais e credenciais do e-SUS foram atendidos com sucesso.',
          icon: 'success',
        })
      } else {
        openChecklistModal(checklist.pendencias)
      }
    } catch (err) {
      if (isPermissaoNegadaEsus(err)) {
        alertarSemPermissao()
      } else {
        showError(err)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleToggleAtivacao = async () => {
    if (!config) return

    if (!podeEditar) {
      alertarSemPermissao()
      return
    }

    if (config.ativo) {
      const isConfirmed = await showConfirmAlert({
        title: 'Desativar Integração?',
        text: 'A transmissão de fichas ao PEC e-SUS APS será interrompida para esta unidade.',
        confirmButtonText: 'Sim, desativar',
      })
      if (!isConfirmed) return

      setLoading(true)
      try {
        const updated = await desativarEsusIntegracao(empresaId, unidadeId)
        onConfigChanged(updated)
        showAlert({
          title: 'Sucesso!',
          text: 'Integração e-SUS desativada com sucesso.',
          icon: 'success',
        })
      } catch (err) {
        if (isPermissaoNegadaEsus(err)) {
          alertarSemPermissao()
        } else {
          showError(err)
        }
      } finally {
        setLoading(false)
      }
    } else {
      const isConfirmed = await showConfirmAlert({
        title: 'Ativar Integração e-SUS?',
        text: 'O sistema executará a validação do Gate de Ativação cadastral antes de liberar a transmissão.',
        icon: 'question',
        confirmButtonText: 'Sim, ativar',
      })
      if (!isConfirmed) return

      setLoading(true)
      try {
        const updated = await ativarEsusIntegracao(empresaId, unidadeId)
        onConfigChanged(updated)
        showAlert({
          title: 'Integração Ativada!',
          text: 'A integração e-SUS APS foi ativada com sucesso para esta unidade.',
          icon: 'success',
        })
      } catch (err: any) {
        const pendencias = err?.response?.data?.data?.pendencias || err?.response?.data?.pendencias
        if (Array.isArray(pendencias) && pendencias.length > 0) {
          openChecklistModal(pendencias)
        } else if (isPermissaoNegadaEsus(err)) {
          alertarSemPermissao()
        } else {
          showError(err)
        }
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <div className='card card-flush mb-4 shadow-sm'>
      <div className='card-body p-5 d-flex flex-wrap align-items-center justify-content-between gap-3'>
        <div className='d-flex flex-column'>
          <span
            className='fs-8 fw-bold text-gray-500 text-uppercase'
            style={{ letterSpacing: 0.5 }}
          >
            Status da Integração e-SUS
          </span>
          <div className='d-flex align-items-center gap-2 mt-1'>
            <span
              className={`bullet bullet-dot w-8px h-8px rounded-circle ${
                config?.ativo ? 'bg-success' : 'bg-danger'
              }`}
            />
            <span className='fs-4 fw-bolder text-gray-900'>
              {config?.ativo ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        </div>
        <div className='d-flex gap-3'>
          {/*
            `btn-outline-primary` em vez de `btn-light-primary`: o fundo `-light` é um tom fixo
            (`--bs-primary-light: #f1faff`) que não acompanha a cor dinâmica da marca nem o tema
            escuro — combinado com `--bs-primary` (dinâmico) o resultado destoa/perde contraste.
            O outline usa fundo transparente e cor única (`--bs-primary`) pra borda/texto/ícone,
            então sempre casa com a marca atual em qualquer tema.
          */}
          <Button
            type={ButtonType.Button}
            onClick={handleVerificarChecklist}
            className='btn-outline btn-outline-primary rounded-pill'
            size='btn-sm'
            disabled={loading}
          >
            <KTSVG path={IconPaths.Check} className='svg-icon-4 me-1' />
            Verificar Checklist
          </Button>
          {podeEditar && (
            <Button
              type={ButtonType.Button}
              onClick={handleToggleAtivacao}
              className={`rounded-pill ${config?.ativo ? 'btn-light-danger' : 'btn-success'}`}
              size='btn-sm'
              disabled={loading}
            >
              {loading ? (
                <LoadingIndicator />
              ) : (
                <>
                  <span className='me-1'>⚡</span>
                  {config?.ativo ? 'Desativar Integração' : 'Ativar Integração'}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
